import React, { useContext, useRef, useState } from 'react';
import { Context } from '@front10/helpers/dist/Context';
import { ThemeProvider } from '@front10/base-elements';
import { EmotionCache } from '../../containers/_commons/emotionCache';
import { isSSR } from '@front10/utils';
/**
 * Atoms
 */
import { FilterButton } from '../../lab/FilterButton';
import { Panels } from '../../containers/VerticalBookingContainer/Panels';
import { FlexibleFilterContainer } from '../../lab/FlexibleFilterContainer';
import Icon from '../../elements/Icon';
import nanoid from 'nanoid';
import { strFormat } from '../../../helpers/utils';
/**
 * Controls
 */
import Rating from '../Rating';

const RatingFilter = (props, ref) => {
  const [id] = React.useState(() => nanoid());
  const {
    onChange,
    value,
    containerSettings,
    starCount,
    fillStars,
    emptyStars,
    halfStars,
    enabledDoneButton = false,
    enabledClearButton = false,
    showSeparator = false,
    disabled,
    filterButtonIconProps,
    doneButtonLabelKey = 'cmp-rating-filter-done-button',
    clearButtonLabelKey = 'cmp-rating-filter-clear-button',
    titleKey = 'cmp-filter-rating-title',
    popover = {},
    halfStar = false,
    minValue = 0
  } = props;

  const [ratingValue, setRatingValue] = useState(minValue);
  const flexibleRef = useRef();
  const ratingRef = useRef();

  const {
    translate,
    direction,
    theme: { designTokens }
  } = useContext(Context);

  const update = React.useCallback(() => {
    setRatingValue(Boolean(value) ? value.minimum : 0);
  }, [value]);

  const disabledButton = React.useMemo(() => {
    let disabled = true;
    if (minValue > 0 && value) {
      let val = value?.minimum ? value.minimum !== value?.maximum : true;
      disabled = val && ratingValue === minValue && disabled;
    } else {
      disabled = !Boolean(value) && ratingValue === 0 && disabled;
    }
    return disabled;
  }, [minValue, value, ratingValue]);

  React.useEffect(() => {
    update();
  }, [value, update]);

  React.useImperativeHandle(
    ref,
    () => ({
      focusToggleElement: () => {
        if (!isSSR()) {
          document.querySelector(`#toggleElementId-${id}`).focus();
        }
      }
    }),
    [id]
  );
  const onStarClick = (nextValue, prevValue, name, e) => {
    if (halfStar && e) {
      const xPos =
        (e.pageX - e.currentTarget.getBoundingClientRect().left) / e.currentTarget.offsetWidth;

      if (xPos <= 0.5) {
        nextValue -= 0.5;
      }
    }
    if (minValue > 0 && nextValue < minValue) {
      nextValue = minValue;
    }

    setRatingValue(nextValue);

    if (!enabledDoneButton) {
      onChange({ minimum: nextValue, maximum: nextValue });
    }
  };

  const activeFilter = React.useMemo(() => {
    let active = false;

    if (minValue > 0) {
      active = value?.minimum ? value.minimum !== minValue : false;
    } else {
      active = Boolean(value);
    }
    return active;
  }, [minValue, value]);

  var timer;
  return (
    <EmotionCache direction={direction}>
      <ThemeProvider theme={designTokens}>
        <FlexibleFilterContainer
          ref={flexibleRef}
          saveOnClose={false}
          variant="layout.popover"
          label={translate(titleKey, 'Rating')}
          title={translate(titleKey, 'Rating')}
          disabledClearButton={disabledButton}
          disabledDoneButton={disabledButton}
          enabledDoneButton={enabledDoneButton}
          enabledClearButton={enabledClearButton}
          showSeparator={showSeparator}
          customAccessibilityToggleElement={true}
          labelBottomBtn={translate(doneButtonLabelKey, 'Done')}
          labelClearBtn={translate(clearButtonLabelKey, 'Clear')}
          onClearBtn={() => {
            onChange(minValue ? { minimum: minValue, maximum: starCount } : undefined);
            setRatingValue(minValue ? minValue : 0);
          }}
          onDismiss={() => {
            update();
          }}
          onOpenPopup={() => {
            setTimeout(() => ratingRef.current?.focusToggleElement(), 100);
            update();
          }}
          toggleElement={
            <FilterButton
              iconSettings={filterButtonIconProps}
              value={
                value
                  ? strFormat(translate('cmp-trip-duration-star', `{0} star`), [value.minimum])
                  : translate(titleKey, 'Number of stars')
              }
              active={activeFilter}
              disabled={disabled} // used to disabled the component
              onFocus={() => {
                if (flexibleRef.current.isOpen) {
                  timer = setTimeout(() => {
                    flexibleRef.current.close();
                  }, 150);
                }
              }}
              onBlur={() => {
                clearTimeout(timer);
              }}
            />
          }
          onSave={() => onChange({ minimum: ratingValue, maximum: ratingValue })}
          {...popover}
        >
          <Panels showSeparator={false}>
            <Rating
              ref={ratingRef}
              containerSettings={containerSettings}
              starCount={starCount}
              minValue={minValue}
              title={translate('cmp-number-stars-label', 'Number of stars')}
              ariaLabel={strFormat(
                translate('cmp-number-nights-aria-label', `Number of stars. {0} star selected`),
                [ratingValue]
              )}
              value={ratingValue}
              onStarClick={onStarClick}
              renderStarIcon={(index, value) => {
                const settings = index <= value ? fillStars : emptyStars;
                return <Icon {...settings} />;
              }}
              halfStar={halfStar}
              renderStarIconHalf={() => {
                return <Icon {...halfStars} />;
              }}
            />
          </Panels>
        </FlexibleFilterContainer>
      </ThemeProvider>
    </EmotionCache>
  );
};

export default React.forwardRef(RatingFilter);
